import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ShareButton from "@/components/ShareButton";
import AiIntro from "@/components/AiIntro";
import { Leaf, SpinnerGap, MapPin } from "@phosphor-icons/react";

const PAGE = 24;

export default function NaturePulse() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState("");

  useEffect(() => {
    api.get("/public/nature/types").then((r) => setTypes(r.data.types || [])).catch(() => {});
  }, []);

  const load = useCallback(async (reset) => {
    setLoading(true);
    const offset = reset ? 0 : photos.length;
    try {
      const tq = activeType ? `&type=${encodeURIComponent(activeType)}` : "";
      const r = await api.get(`/public/nature?limit=${PAGE}&offset=${offset}${tq}`);
      setTotal(r.data.total || 0);
      setPhotos((prev) => (reset ? r.data.photos : [...prev, ...r.data.photos]));
    } catch { /* */ }
    setLoading(false);
  }, [photos.length, activeType]);

  useEffect(() => { load(true); }, [activeType]); // eslint-disable-line

  const TYPE_LABEL = { flower: "Flowers", tree: "Trees", bird: "Birds", insect: "Insects", animal: "Animals", fungi: "Fungi", plant: "Plants" };

  return (
    <PublicLayout>
      <section className="bg-gradient-to-b from-emerald-950 to-emerald-800 text-white">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold backdrop-blur">
            <Leaf size={14} weight="fill" /> NaturePulse
          </span>
          <h1 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            Wildlife &amp; nature,<br />identified by AI
          </h1>
          <p className="mt-3 max-w-xl text-base text-emerald-100/90">
            Every plant, bird, insect and animal our community photographs — recognised with common and scientific names,
            mapped by place, and ready for <a href="https://naturepulse.net" className="underline">naturepulse.net</a>.
          </p>
          <p className="mt-4 text-sm text-emerald-200/80">{total.toLocaleString()} nature photos</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <AiIntro place="Nature" kind="category" label="About nature & wildlife photography" className="mb-6" />
        {types.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="nature-type-chips">
            <button onClick={() => setActiveType("")} data-testid="nature-type-all"
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${!activeType ? "bg-emerald-600 text-white" : "bg-white text-zinc-600 ring-1 ring-border hover:ring-emerald-500"}`}>
              All
            </button>
            {types.map((t) => (
              <button key={t.type} onClick={() => setActiveType(t.type)} data-testid={`nature-type-${t.type}`}
                className={`rounded-full px-4 py-1.5 text-sm font-bold capitalize transition-colors ${activeType === t.type ? "bg-emerald-600 text-white" : "bg-white text-zinc-600 ring-1 ring-border hover:ring-emerald-500"}`}>
                {TYPE_LABEL[t.type] || t.type} <span className="opacity-60">{t.count}</span>
              </button>
            ))}
          </div>
        )}
        {photos.length === 0 && !loading ? (
          <div className="grid place-items-center py-20 text-center text-muted-foreground">
            <Leaf size={40} weight="light" className="text-emerald-300" />
            <p className="mt-3 font-display text-lg font-bold text-zinc-700">No nature photos yet</p>
            <p className="text-sm">Publish nature shots and they'll appear here automatically.</p>
          </div>
        ) : (
          <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4" data-testid="nature-grid">
            {photos.map((p) => {
              const species = (p.nature || []).slice(0, 4);
              return (
                <Link key={p.id} to={`/p/${p.id}`} data-testid={`nature-grid-photo-${p.id}`}
                  className="group relative block break-inside-avoid overflow-hidden rounded-lg bg-zinc-100 shadow-sm ring-1 ring-black/5">
                  <img src={assetUrl(p.image_url)} alt={p.title || "nature"} loading="lazy"
                    className="w-full transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div className="absolute right-2 top-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <ShareButton id={p.id} title={p.title} />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3">
                    <p className="line-clamp-1 text-sm font-bold text-white">{p.title || "Untitled"}</p>
                    {p.location?.city && (
                      <p className="flex items-center gap-1 text-xs text-white/80">
                        <MapPin size={11} weight="fill" />{p.location.city}{p.location.country ? `, ${p.location.country}` : ""}
                      </p>
                    )}
                    {species.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {species.map((s, i) => (
                          <span key={i} title={s.scientific_name || ""}
                            role="link"
                            data-testid={`species-chip-${(s.common_name || s.scientific_name || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            onClick={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              if (s.common_name) navigate(`/species/${encodeURIComponent(s.common_name)}`);
                            }}
                            className="cursor-pointer rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white transition-colors hover:bg-emerald-400">
                            {s.common_name || s.scientific_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {loading && <div className="mt-8 grid place-items-center"><SpinnerGap size={30} className="animate-spin text-emerald-600" weight="bold" /></div>}

        {!loading && photos.length < total && (
          <div className="mt-8 grid place-items-center">
            <button data-testid="nature-load-more" onClick={() => load(false)}
              className="rounded-full border border-emerald-600 px-6 py-2.5 text-sm font-bold text-emerald-700 hover:bg-emerald-50">
              Load more ({(total - photos.length).toLocaleString()} more)
            </button>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
